// Centre-modal overlay wrapper for the reusable Customer 360 (Phase 3).
// Mounts CRMPerson360 in `overlay` mode inside a scrim + scrollable panel so
// pipelines keep their filters/scroll while viewing a customer profile.
import { useEffect } from "react";
import { createPortal } from "react-dom";
import CRMPerson360 from "../pages/crm/CRMPerson360";
import ErrorBoundary from "./ErrorBoundary";

export default function Person360Overlay({ personId, onClose }) {
  useEffect(() => {
    if (!personId) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [personId, onClose]);

  if (!personId) return null;

  return createPortal(
    <div
      onClick={onClose}
      className="fixed inset-0 z-[120] flex items-start justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6 overflow-y-auto"
      data-testid="person360-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-6xl my-2 rounded-2xl border border-white/10 bg-[#0B1120] shadow-[0_30px_90px_rgba(0,0,0,0.75)] p-5 sm:p-7"
        data-testid="person360-overlay-panel"
      >
        <ErrorBoundary
          resetKey={personId}
          fallback={(err) => (
            <div className="text-center py-12" data-testid="person360-error">
              <div className="text-orange-300 text-sm mb-1">Couldn’t load this customer profile</div>
              <div className="text-[11px] text-slate-500 font-mono break-all mb-5 max-w-lg mx-auto">
                {String(err?.message || err)}
              </div>
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg border border-white/15 text-slate-200 hover:bg-white/5 text-sm"
                data-testid="person360-error-close"
              >
                Close
              </button>
            </div>
          )}
        >
          <CRMPerson360 personIdProp={personId} mode="overlay" onClose={onClose} />
        </ErrorBoundary>
      </div>
    </div>,
    document.body,
  );
}
